import type { IngredientCategory, IngredientRef, Season } from '@familychef/shared';
import {
  INGREDIENT_CATEGORIES,
  INGREDIENT_CATEGORY_LABELS,
  SEASONS,
  SEASON_LABELS,
} from '@familychef/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';

interface IngredientFilters {
  search: string;
  category: IngredientCategory | '';
  season: Season | '';
}

interface PaginatedResult {
  data: IngredientRef[];
  total: number;
}

export function useIngredients(filters: IngredientFilters, page: number, pageSize: number = 50) {
  return useQuery<PaginatedResult>({
    queryKey: ['ingredients', filters, page, pageSize],
    queryFn: async () => {
      let query = supabase
        .from('ingredient_refs')
        .select('*', { count: 'exact' })
        .order('name', { ascending: true })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (filters.search) {
        query = query.ilike('name', `%${filters.search}%`);
      }
      if (filters.category) {
        query = query.eq('category', filters.category);
      }
      if (filters.season) {
        query = query.contains('seasons', [filters.season]);
      }

      const { data, count, error } = await query;
      if (error) throw error;
      return { data: (data ?? []) as IngredientRef[], total: count ?? 0 };
    },
  });
}

export function useIngredient(id: string) {
  return useQuery<IngredientRef>({
    queryKey: ['ingredient', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ingredient_refs')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as IngredientRef;
    },
    enabled: !!id,
  });
}

type IngredientCreate = Omit<IngredientRef, 'id' | 'created_at' | 'updated_at'>;

export function useCreateIngredient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: IngredientCreate) => {
      const { error } = await supabase.from('ingredient_refs').insert(data);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ingredients'] }),
  });
}

export function useUpdateIngredient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<IngredientCreate> }) => {
      const { error } = await supabase.from('ingredient_refs').update(data).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ingredients'] }),
  });
}

export function useDeleteIngredient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('ingredient_refs').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ingredients'] }),
  });
}

export { INGREDIENT_CATEGORIES, INGREDIENT_CATEGORY_LABELS, SEASONS, SEASON_LABELS };
export type { IngredientFilters, IngredientCategory, Season };
